import { injectUrlViewState } from '../../../../utils/url-view-state.util';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkDrag, CdkDragEnd, DragDropModule } from '@angular/cdk/drag-drop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { TableService } from '../../../../services/table.service';
import { RestaurantService } from '../../../../services/restaurant.service';
import { FNFResponse } from '../../../../models/response';
import {
  DEFAULT_ZONE_LAYOUT,
  GridPoint,
  Table,
  TableLayout,
  TableLayoutItem,
  TableZone,
  ZoneLayout,
} from '../../../../models/table';
import { TableGlyphComponent } from './table-glyph.component';
import {
  CellRect,
  MAX_GRID,
  MIN_GRID,
  cellPxFor,
  clampRect,
  defaultTableSize,
  findFreeCell,
  layoutRect,
  normalizeRotation,
  outlineProblem,
  pointInPolygon,
  polygonPoints,
  polygonWithinGrid,
  rectInsidePolygon,
  requiredGrid,
  snapPointToCorner,
  snapToGrid,
} from './floor-plan-grid.util';

const ROTATE_STEP = 15;

interface PlacedTable {
  table: Table;
  rect: CellRect;
  rotation: number;
  /** Footprint pokes outside the zone outline (warning only, still saveable). */
  outside: boolean;
  /** Pixel position for cdkDragFreeDragPosition; cached so a new object is
   *  only handed to the CDK when the layout actually changed. */
  pos: { x: number; y: number };
}

/**
 * Per-zone floor-plan editor. Tables are dragged, resized and rotated on a
 * grid whose cell size adapts to the container; positions are kept in whole
 * cells (see `models/table.ts`) and saved in one bulk request.
 *
 * Edits live in `draft` until Save, keyed by table id, so the parent can
 * refetch tables (for example after a 409) without losing unsaved moves.
 */
@Component({
  selector: 'app-floor-plan',
  standalone: true,
  imports: [CommonModule, FormsModule, DragDropModule, MatTooltipModule, TableGlyphComponent],
  templateUrl: './floor-plan.component.html',
  styleUrl: './floor-plan.component.css',
})
export class FloorPlanComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() tables: Table[] = [];
  @Input() zones: TableZone[] = [];
  /** Tables whose name does not match are dimmed on the canvas. */
  @Input() searchQuery = '';
  /** Emitted after a save (or a conflict) so the parent refetches quietly. */
  @Output() changed = new EventEmitter<void>();

  @ViewChild('canvasWrap') canvasWrap?: ElementRef<HTMLElement>;
  @ViewChild('canvas') canvas?: ElementRef<HTMLElement>;

  private tableService = inject(TableService);
  private restaurantService = inject(RestaurantService);
  private toastr = inject(ToastrService);
  private spinner = inject(NgxSpinnerService);
  private zone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  /** The zone tab lives in the page URL (?zone=<id>), so a browser reload
   * reopens the same zone. */
  private urlState = injectUrlViewState();

  selectedZoneId: string | null = null;
  selectedTableId: string | null = null;
  cellPx = 20;
  grid: ZoneLayout = { ...DEFAULT_ZONE_LAYOUT };
  /** Pending grid size in the toolbar inputs until "Apply". */
  gridDraft: ZoneLayout = { ...DEFAULT_ZONE_LAYOUT };
  placed: PlacedTable[] = [];
  unplaced: Table[] = [];
  dragging = false;
  saving = false;
  /** Tracing the zone outline: canvas clicks add corners, tables are frozen. */
  shapeMode = false;
  outlineDraft: GridPoint[] = [];
  private draggingVertex: number | null = null;

  /** Unsaved layout changes by table id; null = unplace. */
  private draft = new Map<string, TableLayout | null>();
  private posCache = new Map<string, { x: number; y: number }>();
  private resizeObserver?: ResizeObserver;
  private resizing: {
    tableId: string;
    startX: number;
    startY: number;
    startRect: CellRect;
  } | null = null;

  readonly minGrid = MIN_GRID;
  readonly maxGrid = MAX_GRID;

  get dirtyCount(): number {
    return this.draft.size;
  }

  get selectedZone(): TableZone | null {
    return this.zones.find((z) => this.zoneId(z) === this.selectedZoneId) ?? null;
  }

  get selectedPlaced(): PlacedTable | null {
    return this.placed.find((p) => p.table.table_id === this.selectedTableId) ?? null;
  }

  get canvasWidth(): number {
    return this.grid.columns * this.cellPx;
  }

  get canvasHeight(): number {
    return this.grid.rows * this.cellPx;
  }

  /** Outline being shown: the draft while tracing, else the saved one. */
  get outline(): GridPoint[] | null {
    if (this.shapeMode) {
      return this.outlineDraft;
    }
    const saved = this.selectedZone?.layout?.outline;
    return saved && saved.length >= 3 ? saved : null;
  }

  /** A saved, closed outline exists for the zone (draft excluded). */
  get hasSavedOutline(): boolean {
    return (this.selectedZone?.layout?.outline?.length ?? 0) >= 3;
  }

  get outlinePoints(): string {
    return polygonPoints(this.outline ?? [], this.cellPx);
  }

  /** Full canvas minus the outline, painted with the evenodd rule. */
  get outsideMaskPath(): string {
    const outline = this.outline;
    if (!outline || outline.length < 3) {
      return '';
    }
    const w = this.canvasWidth;
    const h = this.canvasHeight;
    const inner = outline.map((p) => `${p.x * this.cellPx} ${p.y * this.cellPx}`).join(' L ');
    return `M 0 0 H ${w} V ${h} H 0 Z M ${inner} Z`;
  }

  get outsideCount(): number {
    return this.placed.filter((p) => p.outside).length;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['zones'] || changes['tables']) {
      this.ensureZoneSelected();
      this.syncGridFromZone();
    }
    this.recompute();
  }

  ngAfterViewInit(): void {
    const el = this.canvasWrap?.nativeElement;
    if (!el) {
      return;
    }
    // The container width is only known once the view exists, so the cell
    // size (and every binding derived from it) changes after the first
    // check; flush that change now rather than tripping NG0100.
    if (this.updateCellPx(el.clientWidth)) {
      this.cdr.detectChanges();
    }
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? el.clientWidth;
        this.zone.run(() => this.updateCellPx(width));
      });
      this.resizeObserver.observe(el);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  // ------------------------------------------------------------ zones

  zoneId(zone: TableZone | null | undefined): string | null {
    if (!zone) {
      return null;
    }
    return String((zone as any).zone_id ?? zone.id ?? zone.zone_name);
  }

  zoneTableCount(zone: TableZone): number {
    const id = this.zoneId(zone);
    return this.tables.filter((t) => this.zoneId(t.zone) === id).length;
  }

  selectZone(zone: TableZone): void {
    this.selectedZoneId = this.zoneId(zone);
    this.urlState.set('zone', this.selectedZoneId);
    this.selectedTableId = null;
    this.syncGridFromZone();
    this.recompute();
    this.updateCellPx(this.canvasWrap?.nativeElement.clientWidth ?? 0);
  }

  private ensureZoneSelected(): void {
    const ids = this.zones.map((z) => this.zoneId(z));
    if (!this.selectedZoneId || !ids.includes(this.selectedZoneId)) {
      const fromUrl = this.urlState.get('zone');
      this.selectedZoneId = fromUrl && ids.includes(fromUrl) ? fromUrl : (ids[0] ?? null);
    }
  }

  private syncGridFromZone(): void {
    const layout = this.selectedZone?.layout;
    this.grid = layout
      ? { columns: Number(layout.columns), rows: Number(layout.rows) }
      : { ...DEFAULT_ZONE_LAYOUT };
    this.gridDraft = { ...this.grid };
    this.shapeMode = false;
    this.outlineDraft = [];
  }

  /** PATCHes the zone's layout and mirrors it locally on success. */
  private saveZoneLayout(layout: ZoneLayout, successMessage: string, onDone?: () => void): void {
    const zone = this.selectedZone;
    if (!zone) {
      return;
    }
    this.spinner.show();
    this.restaurantService.patchRestaurantZone({ layout }, zone.id).subscribe({
      next: (response: FNFResponse<any>) => {
        this.spinner.hide();
        if (!response?.data && response?.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          return;
        }
        zone.layout = layout;
        this.grid = { columns: layout.columns, rows: layout.rows };
        this.gridDraft = { ...this.grid };
        onDone?.();
        this.updateCellPx(this.canvasWrap?.nativeElement.clientWidth ?? 0);
        this.recompute();
        this.toastr.success(successMessage, 'Success');
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('Unable to update the zone. Please try again later.', 'Error');
      },
    });
  }

  /** Persists a new grid size on the zone, refusing to cut off placed tables. */
  applyGrid(): void {
    const zone = this.selectedZone;
    if (!zone) {
      return;
    }
    const columns = Math.round(Number(this.gridDraft.columns));
    const rows = Math.round(Number(this.gridDraft.rows));
    if (
      !Number.isFinite(columns) ||
      !Number.isFinite(rows) ||
      columns < MIN_GRID ||
      columns > MAX_GRID ||
      rows < MIN_GRID ||
      rows > MAX_GRID
    ) {
      this.toastr.error(`Grid size must be between ${MIN_GRID} and ${MAX_GRID}.`, 'Invalid grid');
      return;
    }
    const needed = requiredGrid(this.placed.map((p) => p.rect));
    if (columns < needed.columns || rows < needed.rows) {
      this.toastr.error(
        `Tables extend to ${needed.columns} x ${needed.rows}. Move them first.`,
        'Grid too small',
      );
      return;
    }
    const outline = zone.layout?.outline ?? null;
    if (outline && !polygonWithinGrid(outline, { columns, rows })) {
      this.toastr.error(
        'The zone shape reaches beyond that size. Edit the shape first.',
        'Grid too small',
      );
      return;
    }
    if (columns === this.grid.columns && rows === this.grid.rows) {
      return;
    }

    this.saveZoneLayout({ columns, rows, outline }, 'Zone grid updated');
  }

  // -------------------------------------------------------------- shape

  startShapeEdit(): void {
    this.outlineDraft = (this.selectedZone?.layout?.outline ?? []).map((p) => ({ ...p }));
    this.shapeMode = true;
    this.selectedTableId = null;
    this.recompute();
  }

  cancelShapeEdit(): void {
    this.shapeMode = false;
    this.outlineDraft = [];
    this.recompute();
  }

  undoVertex(): void {
    this.outlineDraft = this.outlineDraft.slice(0, -1);
    this.recompute();
  }

  removeVertex(index: number, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.outlineDraft = this.outlineDraft.filter((_, i) => i !== index);
    this.recompute();
  }

  /** Saves the traced outline on the zone and leaves shape mode. */
  finishShape(): void {
    const problem = outlineProblem(this.outlineDraft);
    if (problem) {
      this.toastr.error(problem, 'Shape incomplete');
      return;
    }
    if (!polygonWithinGrid(this.outlineDraft, this.grid)) {
      this.toastr.error('Every point must be on the grid.', 'Shape incomplete');
      return;
    }
    const outline = this.outlineDraft.map((p) => ({ ...p }));
    this.saveZoneLayout({ ...this.grid, outline }, 'Zone shape saved', () => {
      this.shapeMode = false;
      this.outlineDraft = [];
    });
  }

  /** Drops the outline so the zone is the whole rectangle again. */
  resetShape(): void {
    if (!this.hasSavedOutline) {
      this.cancelShapeEdit();
      return;
    }
    this.saveZoneLayout({ ...this.grid, outline: null }, 'Zone shape reset', () => {
      this.shapeMode = false;
      this.outlineDraft = [];
    });
  }

  startVertexDrag(event: MouseEvent | TouchEvent, index: number): void {
    event.stopPropagation();
    event.preventDefault();
    this.draggingVertex = index;
  }

  /** Canvas-relative pixel position of a pointer event. */
  private canvasPoint(event: MouseEvent | TouchEvent): { x: number; y: number } | null {
    const canvas = this.canvas?.nativeElement;
    if (!canvas) {
      return null;
    }
    const bounds = canvas.getBoundingClientRect();
    const point = this.pointFrom(event);
    return { x: point.x - bounds.left, y: point.y - bounds.top };
  }

  private addVertexAt(event: MouseEvent): void {
    const px = this.canvasPoint(event);
    if (!px) {
      return;
    }
    const corner = snapPointToCorner(px, this.cellPx, this.grid);
    const last = this.outlineDraft[this.outlineDraft.length - 1];
    if (last && last.x === corner.x && last.y === corner.y) {
      return;
    }
    this.outlineDraft = [...this.outlineDraft, corner];
    this.recompute();
  }

  // ------------------------------------------------------- placement

  /** The layout the canvas should show: the draft wins over the saved one. */
  private effectiveLayout(table: Table): TableLayout | null {
    if (this.draft.has(table.table_id)) {
      return this.draft.get(table.table_id) ?? null;
    }
    return table.layout ?? null;
  }

  private setDraft(table: Table, layout: TableLayout | null): void {
    const saved = table.layout ?? null;
    if (this.sameLayout(saved, layout)) {
      // Back to the persisted state: nothing left to save for this table.
      this.draft.delete(table.table_id);
    } else {
      this.draft.set(table.table_id, layout);
    }
    this.recompute();
  }

  private sameLayout(a: TableLayout | null, b: TableLayout | null): boolean {
    if (!a || !b) {
      return a === b;
    }
    return (
      a.x === b.x &&
      a.y === b.y &&
      (a.width ?? null) === (b.width ?? null) &&
      (a.height ?? null) === (b.height ?? null) &&
      (a.rotation ?? 0) === (b.rotation ?? 0)
    );
  }

  private recompute(): void {
    const zoneTables = this.tables.filter((t) => this.zoneId(t.zone) === this.selectedZoneId);
    const placed: PlacedTable[] = [];
    const unplaced: Table[] = [];
    for (const table of zoneTables) {
      const layout = this.effectiveLayout(table);
      if (!layout) {
        unplaced.push(table);
        continue;
      }
      const rect = clampRect(layoutRect(layout, table.shape, table.number_of_seats), this.grid);
      const pos = this.cachedPos(table.table_id, rect.x * this.cellPx, rect.y * this.cellPx);
      placed.push({
        table,
        rect,
        rotation: normalizeRotation(layout.rotation ?? 0),
        pos,
        outside: !rectInsidePolygon(rect, this.outline),
      });
    }
    this.placed = placed;
    this.unplaced = unplaced.sort((a, b) => a.table_name.localeCompare(b.table_name));
    if (this.selectedTableId && !placed.some((p) => p.table.table_id === this.selectedTableId)) {
      this.selectedTableId = null;
    }
  }

  private cachedPos(id: string, x: number, y: number): { x: number; y: number } {
    const cached = this.posCache.get(id);
    if (cached && cached.x === x && cached.y === y) {
      return cached;
    }
    const pos = { x, y };
    this.posCache.set(id, pos);
    return pos;
  }

  /** Returns true when the cell size (and so the canvas) actually changed. */
  private updateCellPx(containerWidth: number): boolean {
    const next = cellPxFor(containerWidth, this.grid.columns);
    if (next === this.cellPx) {
      return false;
    }
    this.cellPx = next;
    this.recompute();
    return true;
  }

  isDimmed(table: Table): boolean {
    const query = this.searchQuery?.trim().toLowerCase();
    return !!query && !(table.table_name || '').toLowerCase().includes(query);
  }

  trackByTableId(_: number, item: PlacedTable | Table): string {
    return 'table' in item ? item.table.table_id : item.table_id;
  }

  // ---------------------------------------------------------- selection

  selectTable(table: Table | null, event?: Event): void {
    if (this.shapeMode) {
      // Let the click reach the canvas so it places a corner instead.
      return;
    }
    event?.stopPropagation();
    this.selectedTableId = table?.table_id ?? null;
  }

  onCanvasClick(event: MouseEvent): void {
    if (this.shapeMode) {
      this.addVertexAt(event);
      return;
    }
    if (!this.dragging) {
      this.selectedTableId = null;
    }
  }

  // --------------------------------------------------------------- drag

  onDragStarted(): void {
    this.dragging = true;
  }

  onDragEnded(event: CdkDragEnd, item: PlacedTable): void {
    const free = event.source.getFreeDragPosition();
    const snapped = clampRect(
      {
        x: snapToGrid(free.x, this.cellPx),
        y: snapToGrid(free.y, this.cellPx),
        width: item.rect.width,
        height: item.rect.height,
      },
      this.grid,
    );
    // Snap the element itself too; the binding alone would not move it when
    // the snapped cell equals the previous one.
    event.source.setFreeDragPosition({ x: snapped.x * this.cellPx, y: snapped.y * this.cellPx });
    this.commitRect(item, snapped);
    // Let the click that follows mouseup pass without clearing selection.
    setTimeout(() => (this.dragging = false));
    this.selectedTableId = item.table.table_id;
  }

  private commitRect(item: PlacedTable, rect: CellRect, rotation = item.rotation): void {
    this.setDraft(item.table, {
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
      rotation,
    });
  }

  // ------------------------------------------------------------- resize

  startResize(event: MouseEvent | TouchEvent, item: PlacedTable): void {
    // Keep the CDK drag on the parent from starting.
    event.stopPropagation();
    event.preventDefault();
    const point = this.pointFrom(event);
    this.resizing = {
      tableId: item.table.table_id,
      startX: point.x,
      startY: point.y,
      startRect: { ...item.rect },
    };
    this.selectedTableId = item.table.table_id;
  }

  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  onPointerMove(event: MouseEvent | TouchEvent): void {
    if (this.draggingVertex !== null) {
      const px = this.canvasPoint(event);
      if (px) {
        const corner = snapPointToCorner(px, this.cellPx, this.grid);
        const current = this.outlineDraft[this.draggingVertex];
        if (current && (current.x !== corner.x || current.y !== corner.y)) {
          this.outlineDraft = this.outlineDraft.map((p, i) =>
            i === this.draggingVertex ? corner : p,
          );
          this.recompute();
        }
      }
      return;
    }
    if (!this.resizing) {
      return;
    }
    const item = this.placed.find((p) => p.table.table_id === this.resizing!.tableId);
    if (!item) {
      return;
    }
    const point = this.pointFrom(event);
    const { startRect, startX, startY } = this.resizing;
    const width = Math.round((startRect.width * this.cellPx + (point.x - startX)) / this.cellPx);
    const height = Math.round((startRect.height * this.cellPx + (point.y - startY)) / this.cellPx);
    const next = clampRect(
      {
        x: startRect.x,
        y: startRect.y,
        width: Math.min(width, this.grid.columns - startRect.x),
        height: Math.min(height, this.grid.rows - startRect.y),
      },
      this.grid,
    );
    if (next.width !== item.rect.width || next.height !== item.rect.height) {
      this.commitRect(item, next);
    }
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  onPointerUp(): void {
    this.resizing = null;
    this.draggingVertex = null;
  }

  /** Exposed for the template's vertex handles. */
  isInsideOutline(point: GridPoint): boolean {
    return pointInPolygon(point, this.outline ?? []);
  }

  private pointFrom(event: MouseEvent | TouchEvent): { x: number; y: number } {
    if ('touches' in event) {
      const touch = event.touches[0] ?? (event as TouchEvent).changedTouches[0];
      return { x: touch?.clientX ?? 0, y: touch?.clientY ?? 0 };
    }
    return { x: event.clientX, y: event.clientY };
  }

  // ------------------------------------------------------------ toolbar

  rotate(delta: number): void {
    const item = this.selectedPlaced;
    if (item) {
      this.commitRect(item, item.rect, normalizeRotation(item.rotation + delta));
    }
  }

  resetRotation(): void {
    const item = this.selectedPlaced;
    if (item && item.rotation !== 0) {
      this.commitRect(item, item.rect, 0);
    }
  }

  nudge(dx: number, dy: number): void {
    const item = this.selectedPlaced;
    if (item) {
      this.commitRect(
        item,
        clampRect({ ...item.rect, x: item.rect.x + dx, y: item.rect.y + dy }, this.grid),
      );
    }
  }

  setSize(field: 'width' | 'height', value: string | number): void {
    const item = this.selectedPlaced;
    const parsed = Math.round(Number(value));
    if (!item || !Number.isFinite(parsed)) {
      return;
    }
    this.commitRect(item, clampRect({ ...item.rect, [field]: parsed }, this.grid));
  }

  unplace(item: PlacedTable): void {
    this.setDraft(item.table, null);
    this.selectedTableId = null;
  }

  /** Puts an unplaced table on the first free spot of the grid. */
  place(table: Table): void {
    const size = defaultTableSize(table.shape, table.number_of_seats);
    const spot = findFreeCell(
      size,
      this.grid,
      this.placed.map((p) => p.rect),
    );
    if (!spot) {
      this.toastr.warning('No free space left on this grid. Enlarge it first.', 'Grid full');
      return;
    }
    this.setDraft(table, { ...spot, ...size, rotation: 0 });
    this.selectedTableId = table.table_id;
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, [contenteditable]')) {
      return;
    }
    if (this.shapeMode) {
      if (event.key === 'Escape') {
        this.cancelShapeEdit();
      } else if (event.key === 'Enter') {
        this.finishShape();
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        this.undoVertex();
      } else {
        return;
      }
      event.preventDefault();
      return;
    }
    if (!this.selectedPlaced) {
      return;
    }
    const step = event.shiftKey ? 5 : 1;
    switch (event.key) {
      case 'ArrowLeft':
        this.nudge(-step, 0);
        break;
      case 'ArrowRight':
        this.nudge(step, 0);
        break;
      case 'ArrowUp':
        this.nudge(0, -step);
        break;
      case 'ArrowDown':
        this.nudge(0, step);
        break;
      case 'r':
      case 'R':
        this.rotate(event.shiftKey ? -ROTATE_STEP : ROTATE_STEP);
        break;
      case 'Delete':
      case 'Backspace':
        this.unplace(this.selectedPlaced);
        break;
      case 'Escape':
        this.selectedTableId = null;
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  get rotateStep(): number {
    return ROTATE_STEP;
  }

  // --------------------------------------------------------------- save

  discard(): void {
    this.draft.clear();
    this.recompute();
  }

  save(): void {
    if (!this.draft.size || this.saving) {
      return;
    }
    const byId = new Map(this.tables.map((t) => [t.table_id, t]));
    const items: TableLayoutItem[] = [...this.draft.entries()].map(([table_id, layout]) => ({
      table_id,
      layout,
      version: byId.get(table_id)?.version ?? null,
    }));

    this.saving = true;
    this.spinner.show();
    this.tableService.saveTableLayout({ tables: items }).subscribe({
      next: (response: FNFResponse<Table[]>) => {
        this.saving = false;
        this.spinner.hide();
        if (!response?.data && response?.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          return;
        }
        this.draft.clear();
        this.toastr.success(
          `Saved positions for ${items.length} table${items.length === 1 ? '' : 's'}`,
          'Layout saved',
        );
        this.changed.emit();
      },
      error: (error: HttpErrorResponse) => {
        this.saving = false;
        this.spinner.hide();
        if (error.status === 409) {
          // Someone else saved first. Keep the draft so the moves survive the
          // refetch and can be retried against the new versions.
          this.toastr.warning(
            'This layout was changed elsewhere. It has been refreshed; review your changes and save again.',
            'Layout changed',
          );
          this.changed.emit();
          return;
        }
        const detail = error.error?.detail?.message || error.error?.message;
        this.toastr.error(detail || 'Unable to save the layout. Please try again later.', 'Error');
      },
    });
  }
}
